import { z } from 'zod';

const MIN_LENGTH = 12;
const MAX_LENGTH = 99;

/**
 * Password schema
 * This is used to validate the password on sign in (for existing users when requirements are not enforced)
 */
export const PasswordSchema = z
  .string()
  .min(MIN_LENGTH, { message: 'auth:errors.minPasswordLength' })
  .max(MAX_LENGTH, { message: 'auth:errors.maxPasswordLength' });

/**
 * Refined password schema with additional requirements
 * This is required to validate the password requirements on sign up and password change
 *
 * Requirements:
 * - At least 1 lowercase letter
 * - At least 1 uppercase letter
 * - At least 1 number
 * - At least 1 special character
 */
export const RefinedPasswordSchema = PasswordSchema.superRefine((val, ctx) =>
  validatePassword(val, ctx),
);

export function refineRepeatPassword(
  data: { password: string; repeatPassword: string },
  ctx: z.RefinementCtx,
) {
  if (data.password !== data.repeatPassword) {
    ctx.addIssue({
      message: 'auth:errors.passwordsDoNotMatch',
      path: ['repeatPassword'],
      code: 'custom',
    });
  }

  return true;
}

function validatePassword(password: string, ctx: z.RefinementCtx) {
  if (!/[a-z]/.test(password)) {
    ctx.addIssue({
      message: 'auth:errors.lowercasePassword',
      code: 'custom',
    });
  }

  if (!/[A-Z]/.test(password)) {
    ctx.addIssue({
      message: 'auth:errors.uppercasePassword',
      code: 'custom',
    });
  }

  if (!/\d/.test(password)) {
    ctx.addIssue({
      message: 'auth:errors.minPasswordNumbers',
      code: 'custom',
    });
  }

  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    ctx.addIssue({
      message: 'auth:errors.minPasswordSpecialChars',
      code: 'custom',
    });
  }

  return true;
}
